import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import type { Category, Client, Entry, MonthRollup } from '../../shared/types'
import { formatCents } from '../../shared/money'
import { formatHours2 } from '../../shared/duration'
import { appThisMonth, appToday } from '../datetime'
import Grid from './Grid'
import MonthDashboard from './MonthDashboard'
import InvoiceModal from './InvoiceModal'
import { pushError } from '../toast'

function currentMonth(): string {
  return appThisMonth()
}
function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number)
  const d = new Date(Date.UTC(y, m - 1 + delta, 1))
  return d.toISOString().slice(0, 7)
}
function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export default function Tracker({ clientId }: { clientId: number }) {
  const [client, setClient] = useState<Client | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [month, setMonth] = useState(currentMonth())
  const [entries, setEntries] = useState<Entry[]>([])
  const [rollup, setRollup] = useState<MonthRollup | null>(null)
  const [monthsWithData, setMonthsWithData] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [showInvoice, setShowInvoice] = useState(false)

  // Load client + categories once.
  useEffect(() => {
    ;(async () => {
      try {
        const { client, categories } = await api.getClient(clientId)
        setClient(client)
        setCategories(categories)
        const ms = await api.months(clientId)
        setMonthsWithData(ms)
      } catch (e) {
        pushError(`Couldn't load client: ${(e as Error).message}`)
      }
    })()
  }, [clientId])

  // Load entries + rollup when month changes.
  useEffect(() => {
    if (!client) return
    setLoading(true)
    ;(async () => {
      try {
        const [es, ru] = await Promise.all([api.listEntries(clientId, month), api.rollup(clientId, month)])
        setEntries(es)
        setRollup(ru)
      } catch (e) {
        pushError(`Couldn't load month: ${(e as Error).message}`)
      } finally {
        setLoading(false)
      }
    })()
  }, [clientId, month, client])

  // Refresh rollup after grid edits (debounced).
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onGridChanged = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    refreshTimer.current = setTimeout(async () => {
      try {
        const [ru, ms] = await Promise.all([api.rollup(clientId, month), api.months(clientId)])
        setRollup(ru)
        setMonthsWithData(ms)
      } catch {
        /* rollup refresh is best-effort; errors on saves are surfaced elsewhere */
      }
    }, 500)
  }, [clientId, month])

  if (!client) return <div className="p-10 text-slate-400">Loading…</div>

  const accent = client.accent_color ?? '#3b6ea5'
  const months = Array.from(new Set([currentMonth(), month, ...monthsWithData])).sort().reverse()

  // New grid rows default to today. When an older month is open today isn't in
  // view, so fall back to the 1st of that month — otherwise the new row would
  // save outside the visible month and vanish as you type.
  const defaultDate = appThisMonth() === month ? appToday() : `${month}-01`

  return (
    <div style={{ ['--accent' as any]: accent }} className="min-h-full">
      {/* Accent bar so the user can tell at a glance which client is open. */}
      <div className="h-1" style={{ backgroundColor: accent }} />

      <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
        <header className="mb-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <button className="text-sm text-slate-400 hover:text-slate-700" onClick={() => navigate('#/')}>
                ← Clients
              </button>
              <h1 className="text-xl font-semibold text-slate-900">{client.simple_name}</h1>
            </div>
            <nav className="flex flex-wrap items-center gap-2 text-sm">
              <button
                className="rounded px-2.5 py-1.5 text-slate-600 hover:bg-slate-100"
                onClick={() => navigate(`#/todos/${clientId}`)}
              >
                To-Dos
              </button>
              <button
                className="rounded px-2.5 py-1.5 text-slate-600 hover:bg-slate-100"
                onClick={() => navigate(`#/client/${clientId}/notes`)}
              >
                Notes
              </button>
              <button
                className="rounded px-2.5 py-1.5 text-slate-600 hover:bg-slate-100"
                onClick={() => navigate(`#/client/${clientId}/invoices`)}
              >
                Invoices
              </button>
              <button
                className="rounded px-2.5 py-1.5 text-slate-600 hover:bg-slate-100"
                onClick={() => navigate(`#/client/${clientId}/settings`)}
              >
                Settings
              </button>
              <button
                className="rounded-md px-3 py-1.5 font-medium text-white"
                style={{ backgroundColor: accent }}
                onClick={() => setShowInvoice(true)}
              >
                Generate invoice
              </button>
            </nav>
          </div>

          {/* Month nav + totals */}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <button
                className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
                onClick={() => setMonth((m) => shiftMonth(m, -1))}
                aria-label="Previous month"
              >
                ‹
              </button>
              <select
                className="rounded border border-slate-300 bg-white px-2 py-1 text-sm font-medium text-slate-800"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
              >
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
              <button
                className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
                onClick={() => setMonth((m) => shiftMonth(m, 1))}
                aria-label="Next month"
              >
                ›
              </button>
            </div>
            <div className="tabular flex gap-6 text-sm text-slate-600">
              <span>
                <span className="text-slate-400">Hours </span>
                <b className="text-slate-900">{formatHours2(rollup?.total_minutes ?? 0)}</b>
              </span>
              <span>
                <span className="text-slate-400">Billable </span>
                <b className="text-slate-900">{formatCents(rollup?.total_amount_cents ?? 0)}</b>
              </span>
            </div>
          </div>
        </header>

        {loading ? (
          <div className="py-10 text-slate-400">Loading month…</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_20rem]">
            <div>
              <Grid
                key={`${clientId}-${month}`}
                client={client}
                categories={categories}
                initialEntries={entries}
                defaultDate={defaultDate}
                onChanged={onGridChanged}
              />
            </div>
            <aside>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {monthLabel(month)}
              </h2>
              <MonthDashboard rollup={rollup} />
            </aside>
          </div>
        )}
      </div>

      {showInvoice && (
        <InvoiceModal
          clientId={clientId}
          month={month}
          onClose={() => setShowInvoice(false)}
          onGenerated={(invId) => {
            setShowInvoice(false)
            navigate(`#/invoice/${invId}`)
          }}
        />
      )}
    </div>
  )
}
